import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
    Container,
    Row,
    Col,
    Button,
    Card,
    CardHeader,
    CardBody,
    Table,
    Modal,
    ModalHeader,
    ModalBody,
    Form,
    FormGroup,
    Label,
    Input,
    Spinner,
    Pagination,
    PaginationItem,
    PaginationLink,
    ButtonGroup
} from 'reactstrap';
import { toast } from 'react-toastify';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { FaTimes, FaFilePdf, FaPrint, FaPencilAlt, FaTrash } from 'react-icons/fa';
import { apiURL1 } from '../../config';
import moment from 'moment';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import dayjs from 'dayjs';
import Select from "react-select";



const BankTransactionForm = () => {
    const token = localStorage.getItem("token");

    const [openModal, setOpenModal] = useState(false);
    const [categories, setCategories] = useState([]);
    const [date, setDate] = useState(new Date());
    const [categoryId, setCategoryId] = useState('');
    const [amount, setAmount] = useState('');
    const [voucherNumber, setVoucherNumber] = useState('');

    // State variables for filters
    const [filterVoucherNumber, setFilterVoucherNumber] = useState('');
    const [filterMonth, setFilterMonth] = useState(moment().month() + 1);
    const [filterYear, setFilterYear] = useState(moment().year());
    const [filterDate, setFilterDate] = useState('');

    const [transaction, setTransaction] = useState('deposit');
    const [description, setDescription] = useState('');
    const [bankTransactions, setBankTransactions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [editBankTransaction, setEditBankTransaction] = useState(null);
    const [viewBankTransaction, setViewBankTransaction] = useState(null);
    const [openViewModal, setOpenViewModal] = useState(false);
    const [deleteBankTransactionId, setDeleteBankTransactionId] = useState(null);
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [openingBalance, setOpeningBalance] = useState(0);
    const [madrasaInfo, setMadrasaInfo] = useState({
        schoolName: '',
        schoolAddress: '',
        schoolPhoneNumber: '',
        schoolEmail: ''
    });
    const baseURL = apiURL1;
    const categoryOptions = categories?.map(cat => ({
        value: cat._id,
        label: cat.name
    }));


    useEffect(() => {
        fetchCategories();
        fetchBankTransactions();
        fetchMadrasaInfo();
    }, [page, limit, filterMonth, filterYear, filterVoucherNumber, filterDate]);

    const fetchMadrasaInfo = async () => {
        try {
            const response = await axios.get(`${baseURL}/superadmin/school-settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                },
            });
            const data = response.data;
            if (data && data.length > 0) {
                setMadrasaInfo(data[0]);
            }
        } catch (error) {
            console.error('Error fetching madrasa info:', error);
        }
    };

    const fetchCategories = async () => {
        try {
            setLoading(true);
            const response = await axios.get(`${baseURL}/superadmin/all-expense-categories`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });
            setCategories(response.data?.expenseCategories || []);
        } catch (error) {
            console.error('Error fetching categories:', error);
            toast.error('Error fetching categories');
            setCategories([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchBankTransactions = async () => {
        try {
            setLoading(true);
            let url = `${baseURL}/superadmin/all-bank-records?page=${page}&limit=${limit}`;

            if (filterVoucherNumber) {
                url += `&voucherNumber=${filterVoucherNumber}`;
            }
            if (filterMonth) {
                url += `&month=${filterMonth}`;
            }
            if (filterYear) {
                url += `&year=${filterYear}`;
            }
            if (filterDate) {
                url += `&date=${filterDate}`;
            }

            const response = await axios.get(
                url, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            }
            );
            setBankTransactions(response.data?.bankTransactions || []);
            setOpeningBalance(response.data?.openingBalance || 0);
            setTotal(response.data?.total || 0);
            setTotalPages(response.data?.totalPages || 1);
        } catch (error) {
            console.error('Error fetching bank transactions:', error);
            toast.error('Error fetching bank transactions');
            setBankTransactions([]);
            setOpeningBalance(0);
            setTotal(0);
            setTotalPages(1);
        } finally {
            setLoading(false);
        }
    };

    const handleOpenModal = () => {
        setOpenModal(true);
        setEditBankTransaction(null);
        setDate(new Date());
        setCategoryId('');
        setAmount('');
        setTransaction('deposit');
        setDescription('');
    };

    const handleCloseModal = () => {
        setOpenModal(false);
    };

    const handleSaveBankTransaction = async () => {
        try {
            setLoading(true);
            const transactionData = {
                voucherNumber: voucherNumber,
                date: date.toISOString().split('T')[0],
                amount: parseFloat(amount),
                description,
                categoryId,
                transaction,
            };

            if (editBankTransaction) {
                await axios.put(
                    `${baseURL}/superadmin/bank-records/${editBankTransaction._id}`,
                    transactionData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
                );
                toast.success('Bank transaction updated successfully');
            } else {
                await axios.post(`${baseURL}/superadmin/bank-records`, transactionData, {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                });
                toast.success('Bank transaction added successfully');
            }

            fetchBankTransactions();
            handleCloseModal();
        } catch (error) {
            console.error('Error saving bank transaction:', error);
            toast.error('Error saving bank transaction');
        } finally {
            setLoading(false);
        }
    };

    const handleEditBankTransaction = (bankTransaction) => {
        setEditBankTransaction(bankTransaction);
        setVoucherNumber(bankTransaction.voucherNumber || 'N/A')

        setDate(new Date(bankTransaction.date));
        setCategoryId(bankTransaction.categoryId?._id);
        setAmount(bankTransaction.amount);
        setTransaction(bankTransaction.transaction);
        setDescription(bankTransaction.description);
        setOpenModal(true);
    };

    const handleDeleteBankTransaction = async () => {
        try {
            setLoading(true);
            await axios.delete(`${baseURL}/superadmin/bank-records/${deleteBankTransactionId}`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                }
            });
            fetchBankTransactions();
            handleCloseDeleteModal();
            toast.success('Bank transaction deleted successfully');
        } catch (error) {
            console.error('Error deleting bank transaction:', error);
            toast.error('Error deleting bank transaction');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenViewModal = (bankTransaction) => {
        setViewBankTransaction(bankTransaction);
        setOpenViewModal(true);
    };

    const handleCloseViewModal = () => {
        setOpenViewModal(false);
    };

    const handleOpenDeleteModal = (id) => {
        setDeleteBankTransactionId(id);
        setOpenDeleteModal(true);
    };

    const handleCloseDeleteModal = () => {
        setDeleteBankTransactionId(null);
        setOpenDeleteModal(false);
    };

    const renderPagination = () => {
        const paginationItems = [];
        for (let i = 1; i <= totalPages; i++) {
            paginationItems.push(
                <PaginationItem key={i} active={i === page}>
                    <PaginationLink onClick={() => setPage(i)}>{i}</PaginationLink>
                </PaginationItem>,
            );
        }
        return (
            <Pagination style={{ justifyContent: 'flex-end' }}>
                <PaginationItem disabled={page === 1}>
                    <PaginationLink previous onClick={() => setPage(page - 1)} />
                </PaginationItem>
                {paginationItems}
                <PaginationItem disabled={page === totalPages}>
                    <PaginationLink next onClick={() => setPage(page + 1)} />
                </PaginationItem>
            </Pagination>
        );
    };

    const generateReport = () => {
        const doc = new jsPDF();

        // Add logo and school information
        const img = new Image();
        img.src = "https://res.cloudinary.com/djg7v9dna/image/upload/v1742946371/mi0wavjsehps20kdzaet.png";

        doc.addImage(img, 'PNG', 15, 15, 30, 30);

        doc.setFontSize(14);
        doc.text(madrasaInfo.schoolName, 50, 20);

        doc.setFontSize(10);
        doc.text(madrasaInfo.schoolAddress, 50, 27);
        doc.text(`Phone: ${madrasaInfo.schoolPhoneNumber} | Email: ${madrasaInfo.schoolEmail}`, 50, 34);

        // Define the columns for the table
        const columns = ["Voucher Number", "Date", "Category", "Amount", "Transaction", "Description"];

        // Prepare the data for the table
        const data = bankTransactions ? bankTransactions.map(transaction => [
            transaction?.voucherNumber || 'N/A',
            new Date(transaction.date).toLocaleDateString(),
            transaction.categoryId?.name || 'N/A',
            transaction.amount,
            transaction.transaction === 'withdrawal' ? 'Cash Outflow' : 'Cash Inflow',
            transaction.description
        ]) : [];

        // Add the table to the PDF
        doc.autoTable({
            startY: 45,
            head: [columns],
            body: data,
        });

        // Save the PDF
        doc.save('bank_transactions_report.pdf');
    }

    const printReceipt = (bankTransaction) => {
        const voucherNumber = bankTransaction?.voucherNumber || 'N/A';
        const date = dayjs(bankTransaction?.date).format('DD-MM-YYYY') || 'N/A';
        const category = bankTransaction?.categoryId?.name || 'N/A';
        const transactionType = bankTransaction?.transaction === 'withdrawal' ? 'Cash Outflow' : 'Cash Inflow';
        const amount = bankTransaction?.amount || 'N/A';
        const paymentMethod = 'Bank Transaction';
        const description = bankTransaction?.description || 'N/A';
        const amountInWordsText = isNaN(amount) ? amount : amountInWords(parseInt(amount));
        let checkoutBy = 'N/A';
        try {
            const ui = localStorage.getItem('userInfo');
            checkoutBy = (ui && JSON.parse(ui)?.name) || 'N/A';
        } catch (e) { console.error(e); }

        const receiptContent = `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8">
    <title>Bank Transaction Receipt</title>
    <style>
        @page { size: A5; margin: 8mm; }
        * { box-sizing: border-box; }
        body { margin: 0; padding: 0; font-family: sans-serif; font-size: 12px; color: #000; }
        .receipt {
            width: 100%; max-width: 132mm; margin: 0 auto;
            page-break-inside: avoid;
        }
        .header { text-align: center; margin-bottom: 8px; }
        .header img { width: 56px; height: 56px; margin-bottom: 4px; }
        .header h2 { margin: 2px 0; font-size: 14px; }
        .header h3 { margin: 6px 0 2px; font-size: 13px; }
        .header p { margin: 1px 0; font-size: 10px; }
        .header hr { border: 0; border-top: 0.5px solid #000; margin: 6px 0; }
        .row { display: flex; justify-content: space-between; gap: 8px; margin: 4px 0; text-align: left; }
        .field { margin: 4px 0; font-size: 12px; text-align: left; }
        .field span { border-bottom: 1px solid #000; display: inline-block; min-width: 40px; }
        .sigs { display: flex; justify-content: space-between; margin-top: 18px; page-break-inside: avoid; }
        .sig { text-align: center; min-width: 110px; }
        .sig-line { border-top: 1px solid #000; margin-top: 22px; padding-top: 4px; font-size: 11px; }
    </style>
</head>
<body>
    <div class="receipt">
            <div class="header">
                <img src="https://res.cloudinary.com/djg7v9dna/image/upload/v1739198257/Logo/kgamz0yjfr9uy8dsbnur.webp" alt="Madrasa Logo">
                <h2>${madrasaInfo.schoolName || 'N/A'}</h2>
                <p>${madrasaInfo.schoolAddress || 'N/A'}</p>
                <p>Phone: ${madrasaInfo.schoolPhoneNumber || 'N/A'} | Email: ${madrasaInfo.schoolEmail || 'N/A'}</p>
                <hr>
                <h3>BANK TRANSACTION RECEIPT</h3>
            </div>

            <div class="row">
                <p class="field" style="margin:0;"><strong>Voucher No:</strong> <span>${voucherNumber}</span></p>
                <p class="field" style="margin:0;"><strong>Date:</strong> <span>${date}</span></p>
            </div>
            <p class="field"><strong>Payment made to:</strong> <span>${category}</span></p>
            <div class="row">
                <p class="field" style="margin:0;"><strong>Payment Method:</strong> <span>${paymentMethod}</span></p>
                <p class="field" style="margin:0;"><strong>Amount:</strong> <span>${amount}</span></p>
            </div>
            <p class="field"><strong>Amount in Words:</strong> <span>${amountInWordsText}</span></p>
            <p class="field"><strong>Description:</strong> <span>${description}</span></p>

            <div class="sigs">
                <div class="sig"><div class="sig-line"><strong>Received By</strong></div></div>
                <div class="sig"><div class="sig-line"><strong>Checked By</strong></div></div>
                <div class="sig"><div class="sig-line"><strong>Approved By</strong></div></div>
            </div>
    </div>
    <script>
        window.onload = function() {
            // Set print window to maximum size before printing
            window.moveTo(0, 0);
            window.resizeTo(screen.availWidth, screen.availHeight);
            
            // Short delay to ensure the window is resized before printing
            setTimeout(function() {
                window.print();
                // Close window after printing
                window.onfocus = function() { 
                    setTimeout(function() { window.close(); }, 3500);
                }
            }, 200);
        }
    </script>
</body>
</html>
`;

        // Open in a larger window by default
        const printWindow = window.open('', '_blank', `width=${screen.availWidth},height=${screen.availHeight}`);
        printWindow.document.write(receiptContent);
        printWindow.document.close();
    };

    const amountInWords = (num) => {
        // console.log('num is', num);

        const ones = [
            "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"
        ];
        const teens = [
            "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"
        ];
        const tens = [
            "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
        ];

        if (num === 0) return "Zero";
        if (isNaN(num)) return "Invalid Amount";

        let words = "";

        // Handle lakhs (100,000s)
        if (num >= 100000) {
            const lakhsValue = Math.floor(num / 100000);
            words += convertTwoDigits(lakhsValue) + " Lakh ";
            num %= 100000;
        }

        // Handle thousands
        if (num >= 1000) {
            const thousandsValue = Math.floor(num / 1000);
            words += convertTwoDigits(thousandsValue) + " Thousand ";
            num %= 1000;
        }

        // Handle hundreds
        if (num >= 100) {
            words += ones[Math.floor(num / 100)] + " Hundred ";
            num %= 100;
        }

        // Handle remaining tens and ones
        if (num > 0) {
            // Add "and" after hundreds or thousands if there are remaining digits
            if (words.length > 0) {
                words += "and ";
            }
            words += convertTwoDigits(num);
        }

        return words.trim();

        // Helper function to convert two-digit numbers
        function convertTwoDigits(n) {
            if (n === 0) return "";
            if (n < 10) return ones[n];
            if (n >= 10 && n < 20) return teens[n - 10];

            const tensDigit = Math.floor(n / 10);
            const onesDigit = n % 10;

            if (onesDigit === 0) return tens[tensDigit];
            return tens[tensDigit] + " " + ones[onesDigit];
        }
    };


    const handleSearch = () => {
        setPage(1); // Reset to the first page when searching
        fetchBankTransactions();
    };

    const getDaysInMonth = () => {
        const year = filterYear || moment().year();
        const month = filterMonth || moment().month() + 1;
        return moment(`${year}-${month}`, "YYYY-MM").daysInMonth();
    };

    return (
        <Container>
            <Row className="mb-3">
                <Col>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Button color="primary" size="sm" onClick={handleOpenModal}>
                            Add Bank Transaction
                        </Button>
                        <Button color="primary" size="sm" onClick={generateReport} disabled={loading}>
                            {loading ? <Spinner size="sm" /> : <FaFilePdf />} Generate Report
                        </Button>
                    </div>
                </Col>
            </Row>

            {/* Filter Inputs */}
            <Row className="mb-3">
                <Col md="3">
                    <FormGroup>
                        <Label>Voucher Number</Label>
                        <Input
                            type="text"
                            value={filterVoucherNumber}
                            onChange={(e) => setFilterVoucherNumber(e.target.value)}
                        />
                    </FormGroup>
                </Col>
                <Col md="3">
                    <FormGroup>
                        <Label>Month</Label>
                        <Input
                            type="select"
                            value={filterMonth}
                            onChange={(e) => setFilterMonth(e.target.value)}
                        >
                            <option value="">Select Month</option>
                            {[...Array(12)].map((_, i) => {
                                const month = i + 1;
                                const monthName = new Date(2000, i, 1).toLocaleString('default', { month: 'long' });
                                return (
                                    <option key={month} value={month}>{monthName}</option>
                                );
                            })}
                        </Input>
                    </FormGroup>
                </Col>
                <Col md="3">
                    <FormGroup>
                        <Label>Year</Label>
                        <Input
                            type="number"
                            value={filterYear}
                            onChange={(e) => setFilterYear(e.target.value)}
                        />
                    </FormGroup>
                </Col>
                <Col md="3">
                    <FormGroup>
                        <Label>Date</Label>
                        <Input
                            type="select"
                            value={filterDate}
                            onChange={(e) => setFilterDate(e.target.value)}
                        >
                            <option value="">Select Date</option>
                            {[...Array(getDaysInMonth())].map((_, i) => {
                                const day = i + 1;
                                return (
                                    <option key={day} value={day}>{day}</option>
                                );
                            })}
                        </Input>
                    </FormGroup>
                </Col>
                <Col md="3" style={{ display: 'flex', alignItems: 'flex-end' }}>
                    {/* <Button color="primary" onClick={handleSearch}>
                        Search
                    </Button> */}
                </Col>
            </Row>

            <Card>
                <CardHeader>
                    <h3>Bank Transactions</h3>
                    <p>Opening Balance: {openingBalance}</p>
                </CardHeader>
                <CardBody>
                    <div className="table-responsive">
                        <Table>
                            <thead>
                                <tr>
                                    <th style={{ maxWidth: '120px', wordBreak: 'break-word' }}>Voucher Number</th>
                                    <th style={{ maxWidth: '120px', wordBreak: 'break-word' }}>Date</th>
                                    <th style={{ maxWidth: '150px', wordBreak: 'break-word' }}>Category</th>
                                    <th style={{ maxWidth: '100px', wordBreak: 'break-word' }}>Amount</th>
                                    <th style={{ maxWidth: '120px', wordBreak: 'break-word' }}>Transaction</th>
                                    <th style={{ maxWidth: '250px', wordBreak: 'break-word' }}>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bankTransactions && bankTransactions.length > 0 ? (
                                    bankTransactions.map((bankTransaction) => (
                                        <tr key={bankTransaction._id}>
                                            <td style={{ maxWidth: '120px', wordBreak: 'break-word' }}>{bankTransaction?.voucherNumber || 'N/A'}</td>
                                            <td style={{ maxWidth: '120px', wordBreak: 'break-word' }}>
                                                {(() => {
                                                    const d = new Date(bankTransaction.date);
                                                    const day = String(d.getDate()).padStart(2, '0');
                                                    const month = String(d.getMonth() + 1).padStart(2, '0');
                                                    const year = d.getFullYear();
                                                    return `${day}-${month}-${year}`;
                                                })()}
                                            </td>
                                            <td style={{ maxWidth: '150px', wordBreak: 'break-word' }}>{bankTransaction.categoryId?.name}</td>
                                            <td style={{ maxWidth: '100px', wordBreak: 'break-word' }}>{bankTransaction.amount}</td>
                                            <td style={{ maxWidth: '120px', wordBreak: 'break-word' }}>{bankTransaction.transaction === 'withdrawal' ? 'Cash Outflow' : 'Cash Inflow'}</td>
                                            <td style={{ maxWidth: '250px', wordBreak: 'break-word' }}>
                                                <ButtonGroup size="sm">
                                                    <Button color="info" className="mr-1" onClick={() => handleOpenViewModal(bankTransaction)}>
                                                        View
                                                    </Button>
                                                    <Button color="primary" className="mr-1" onClick={() => handleEditBankTransaction(bankTransaction)}>
                                                        <FaPencilAlt />
                                                    </Button>
                                                    <Button color="danger" className="mr-1" onClick={() => handleOpenDeleteModal(bankTransaction._id)}>
                                                        <FaTrash />
                                                    </Button>
                                                    <Button color="secondary" onClick={() => printReceipt(bankTransaction)}>
                                                        <FaPrint />
                                                    </Button>
                                                </ButtonGroup>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="5" className="text-center">No bank transactions found.</td>
                                    </tr>
                                )}
                            </tbody>
                        </Table>
                    </div>
                    <Row>
                        <Col style={{ display: 'flex', justifyContent: 'flex-end' }}>
                            {renderPagination()}
                        </Col>
                    </Row>
                </CardBody>
            </Card>

            <Modal isOpen={openModal} toggle={handleCloseModal}>
                <ModalHeader toggle={handleCloseModal}>
                    {editBankTransaction ? 'Edit Bank Transaction' : 'Add Bank Transaction'}
                </ModalHeader>
                <ModalBody>
                    <Form>
                        {editBankTransaction ? (<FormGroup>
                            <Label>Voucher Number</Label>
                            <Input
                                type="text"
                                value={voucherNumber}
                                readOnly
                            />
                        </FormGroup>) : (<></>)}
                        <FormGroup>
                            <Label>Date</Label>
                            <DatePicker
                                selected={date}
                                onChange={(newDate) => setDate(newDate)}
                                className="form-control"
                                dateFormat="dd-MM-yyyy"
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Category</Label>
                            <Select
                                options={categoryOptions}
                                value={categoryOptions?.find(option => option.value === categoryId)}
                                onChange={(selectedOption) =>
                                    setCategoryId(selectedOption ? selectedOption.value : "")
                                }
                                placeholder="Select Category"
                                isClearable
                                isSearchable
                                className="react-select-container"
                                classNamePrefix="react-select"
                                isDisabled={Boolean(editBankTransaction && editBankTransaction.categoryId?.name === 'Fund Transfer')}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Amount</Label>
                            <Input
                                type="number"
                                value={amount}
                                onChange={(e) => setAmount(e.target.value)}
                            />
                        </FormGroup>
                        <FormGroup>
                            <Label>Transaction Type</Label>
                            <Input
                                type="select"
                                value={transaction}
                                onChange={(e) => setTransaction(e.target.value)}
                                disabled={Boolean(editBankTransaction && editBankTransaction.categoryId?.name === 'Fund Transfer')}
                            >
                                <option value="deposit">Cash Inflow</option>
                                <option value="withdrawal">Cash Outflow</option>
                            </Input>
                        </FormGroup>
                        <FormGroup>
                            <Label>Description</Label>
                            <Input
                                type="textarea"
                                value={description}
                                onChange={(e) => setDescription(e.target.value)}
                            />
                        </FormGroup>
                        <Button color="primary" onClick={handleSaveBankTransaction} disabled={loading}>
                            {loading ? <Spinner size="sm" /> : 'Save'}
                        </Button>
                        <Button color="secondary" onClick={handleCloseModal} className="ms-2">
                            Cancel
                        </Button>
                    </Form>
                </ModalBody>
            </Modal>

            <Modal isOpen={openViewModal} toggle={handleCloseViewModal}>
                <ModalHeader toggle={handleCloseViewModal}>Bank Transaction Details</ModalHeader>
                <ModalBody style={{ overflowWrap: 'break-word' }}>
                    {viewBankTransaction && (
                        <div>
                            <p>
                                <b>Voucher Number:</b> {viewBankTransaction.voucherNumber || 'N/A'}
                            </p>
                            <p>
                                <b>Date:</b>{' '}
                                {(() => {
                                    const d = new Date(viewBankTransaction.date);
                                    const day = String(d.getDate()).padStart(2, '0');
                                    const month = String(d.getMonth() + 1).padStart(2, '0');
                                    const year = d.getFullYear();
                                    return `${day}-${month}-${year}`;
                                })()}
                            </p>
                            <p>
                                <b>Category:</b> {viewBankTransaction.categoryId?.name}
                            </p>
                            <p>
                                <b>Amount:</b> {viewBankTransaction.amount}
                            </p>
                            <p>
                                <b>Transaction:</b> {viewBankTransaction.transaction === 'withdrawal' ? 'Cash Outflow' : 'Cash Inflow'}
                            </p>
                            <p>
                                <b>Description:</b> {viewBankTransaction.description}
                            </p>
                        </div>
                    )}
                </ModalBody>
            </Modal>

            <Modal isOpen={openDeleteModal} toggle={handleCloseDeleteModal}>
                <ModalHeader toggle={handleCloseDeleteModal}>Delete Bank Transaction</ModalHeader>
                <ModalBody>
                    <p>Are you sure you want to delete this bank transaction?</p>
                    <Button color="danger" onClick={handleDeleteBankTransaction} disabled={loading}>
                        {loading ? <Spinner size="sm" /> : 'Delete'}
                    </Button>
                    <Button color="secondary" onClick={handleCloseDeleteModal} className="ms-2">
                        Cancel
                    </Button>
                </ModalBody>
            </Modal>
        </Container>
    );
};

export default BankTransactionForm;
